import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { setLoadReportConfigurationId } from 'actions';
import useDispatch from 'lib/hooks/useDispatch';
import { selectLoadReportConfigurationId } from 'state/reports/selectors';

import type { ReportConfiguration } from 'common/generated/graphql';

export const REPORT_CONFIGURATION_QUERY_PARAMETER_KEY = 'saved-report-id';

type UseSavedReportFromUrlProps = {
  reportConfigurations: ReportConfiguration[];
  selectedReportConfigurationId?: string;
  onSelectReportConfiguration: (reportConfiguration: ReportConfiguration) => void;
};

/**
 * Hook that handles loading a saved report configuration from a URL parameter.
 *
 * This hook watches for the saved report ID parameter in the URL, finds the matching
 * report configuration, applies it, and then removes the parameter from the URL.
 */
export const useLoadSavedReportFromUrl = ({
  reportConfigurations,
  onSelectReportConfiguration,
  selectedReportConfigurationId,
}: UseSavedReportFromUrlProps) => {
  const dispatch = useDispatch();
  const loadReportConfigurationId = useSelector(selectLoadReportConfigurationId);
  const history = useHistory();
  const { location } = history;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const savedReportIdParam = params.get(REPORT_CONFIGURATION_QUERY_PARAMETER_KEY);

    // Track report configuration ID to load to prevent re-applying the same report during:
    // 1. Component re-renders
    // 2. Effect re-runs due to dependency changes
    // 3. Async loading of reportConfigurations
    if (savedReportIdParam) {
      dispatch(setLoadReportConfigurationId(savedReportIdParam));
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    if (loadReportConfigurationId) {
      const reportConfiguration = reportConfigurations.find(
        (config) => config.id === loadReportConfigurationId,
      );

      if (reportConfiguration && selectedReportConfigurationId !== reportConfiguration.id) {
        // Apply the report configuration
        onSelectReportConfiguration(reportConfiguration);

        // Clear the report ID from cache
        dispatch(setLoadReportConfigurationId(null));
      }
    }
  }, [
    loadReportConfigurationId,
    reportConfigurations,
    selectedReportConfigurationId,
    onSelectReportConfiguration,
    dispatch,
    history,
    location,
  ]);
};
